import { Progress } from '@heroui/react';
import { Chip } from '@heroui/chip';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import { ImageType } from '@/shared/types/common';
import { Svg } from '@/shared/ui/Svg';
import ChevronRightIcon from '@/shared/assets/icons/chevron-right.svg';
import { getProgressPercent } from '@/shared/lib/utils/getProgressPercent';

interface HomeProgramItemProps {
  courseItem: {
    typeCourse: string;
    title: string;
    description?: string;
    user?: {
      avatar: ImageType;
      userName: string;
      href: string;
    };
    href: string;
    modules?: {
      completed: number;
      total: number;
    };

    progress?: number;
  };
  isDetailProgress?: boolean;
}

export const ProgramItem = (props: HomeProgramItemProps) => {
  const { courseItem, isDetailProgress = false } = props;
  const { typeCourse, title, description, user, modules, progress, href } = courseItem;

  const moduleLabel =
    modules &&
    `${modules.completed} out of ${modules.total} ${modules.total > 1 ? 'modules' : 'module'} completed`;
  const progressBar =
    progress || (modules ? getProgressPercent(modules.completed, modules.total) : 0);

  return (
    <div
      className={cn(
        'group relative flex flex-col shadow-info-card p-4 rounded-[0.5rem] transition-colors duration-300 hover:bg-neutral-50',
        isDetailProgress ? 'gap-y-4' : 'gap-y-7',
      )}
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <Chip
            className={cn('text-white bg-red', {
              ['bg-[#211D55]']: typeCourse?.toLowerCase() === 'short course',
            })}
          >
            {typeCourse}
          </Chip>
          <Svg
            Icon={ChevronRightIcon}
            className="rounded transition-colors duration-300 group-hover:bg-neutral-100"
          />
        </div>
        <Link
          to={href}
          className="text-lg font-bold mt-2 mb-3 after:absolute after:inset-0 after:rounded-[0.5rem]"
        >
          {title}
        </Link>
        {description && <p className="font-medium mb-3">{description}</p>}
        {user && (
          <div className="relative z-10 self-start">
            <UserAvatar {...user} />
          </div>
        )}
      </div>

      {typeof progress === 'number' && (
        <Progress
          classNames={{
            indicator: 'bg-[#17C964]',
          }}
          size={isDetailProgress ? 'md' : 'sm'}
          value={progressBar}
          label={isDetailProgress && moduleLabel}
          showValueLabel={isDetailProgress}
          aria-label={`Progress: ${progressBar}%`}
        />
      )}
    </div>
  );
};
